import type { SupabaseClient } from '@supabase/supabase-js';
import type { SyncRecord, WorkspaceMemberRow, WorkspaceRow } from '@nexus/store';

// The schema name is configurable per self-hosted project, not the literal "public" type.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SupabaseClientAnySchema = SupabaseClient<any, string>;

export interface RemoteRecord {
  seq: number;
  record: SyncRecord;
}

export interface RemoteContent {
  id: number;
  pageId: string;
  deviceId: string;
  data: Uint8Array;
}

/** What the sync engine needs from the cloud. Implemented for Supabase and for tests. */
export interface Remote {
  userId(): Promise<string | null>;
  pushRecords(records: SyncRecord[]): Promise<void>;
  pullRecords(after: number, limit: number): Promise<RemoteRecord[]>;
  pushContent(
    items: { pageId: string; data: Uint8Array; workspaceId: string | null }[],
    deviceId: string,
  ): Promise<void>;
  pullContent(after: number, limit: number): Promise<RemoteContent[]>;
  uploadFile(path: string, data: Uint8Array, mime: string): Promise<void>;
  downloadFile(path: string): Promise<Uint8Array>;
  /** Shared workspaces (Deel B): the server is authoritative, so this is a full list each round. */
  pullWorkspaces(): Promise<{ workspaces: WorkspaceRow[]; members: WorkspaceMemberRow[] }>;
  /**
   * Everything of one workspace, from the start: after joining it, its older
   * rows lie behind this device's cursors and would never be pulled otherwise.
   */
  pullWorkspaceRecords(workspaceId: string, after: number, limit: number): Promise<RemoteRecord[]>;
  pullWorkspaceContent(workspaceId: string, after: number, limit: number): Promise<RemoteContent[]>;
}

export function toBase64(data: Uint8Array): string {
  let s = '';
  for (let i = 0; i < data.length; i += 0x8000) {
    s += String.fromCharCode(...data.subarray(i, i + 0x8000));
  }
  return btoa(s);
}

export function fromBase64(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

const BUCKET = 'attachments';

/** The Supabase project: `records` + `merge_records()`, `page_updates`, Storage. */
export class SupabaseRemote implements Remote {
  constructor(private readonly client: SupabaseClientAnySchema) {}

  async userId(): Promise<string | null> {
    const { data } = await this.client.auth.getSession();
    return data.session?.user.id ?? null;
  }

  async pushRecords(records: SyncRecord[]): Promise<void> {
    if (records.length === 0) return;
    const rows = records.map((r) => ({
      kind: r.kind,
      id: r.id,
      fields: r.fields,
      field_hlc: r.fieldHlc,
      workspace_id: r.workspaceId ?? null,
    }));
    const { error } = await this.client.rpc('merge_records', { rows });
    if (error) throw new Error(`merge_records: ${error.message}`);
  }

  pullRecords(after: number, limit: number): Promise<RemoteRecord[]> {
    return this.records(after, limit, null);
  }

  pullWorkspaceRecords(workspaceId: string, after: number, limit: number) {
    return this.records(after, limit, workspaceId);
  }

  private async records(
    after: number,
    limit: number,
    workspaceId: string | null,
  ): Promise<RemoteRecord[]> {
    let query = this.client
      .from('records')
      .select('kind, id, fields, field_hlc, server_seq, workspace_id')
      .gt('server_seq', after);
    if (workspaceId) query = query.eq('workspace_id', workspaceId);
    const { data, error } = await query.order('server_seq').limit(limit);
    if (error) throw new Error(`records: ${error.message}`);
    return (data ?? []).map((r) => ({
      seq: Number(r.server_seq),
      record: {
        kind: r.kind,
        id: r.id,
        fields: r.fields,
        fieldHlc: r.field_hlc,
        workspaceId: r.workspace_id,
      },
    }));
  }

  async pushContent(
    items: { pageId: string; data: Uint8Array; workspaceId: string | null }[],
    deviceId: string,
  ): Promise<void> {
    if (items.length === 0) return;
    const { error } = await this.client.from('page_updates').insert(
      items.map((i) => ({
        page_id: i.pageId,
        device_id: deviceId,
        data: toBase64(i.data),
        workspace_id: i.workspaceId,
      })),
    );
    if (error) throw new Error(`page_updates: ${error.message}`);
  }

  pullContent(after: number, limit: number): Promise<RemoteContent[]> {
    return this.content(after, limit, null);
  }

  pullWorkspaceContent(workspaceId: string, after: number, limit: number) {
    return this.content(after, limit, workspaceId);
  }

  private async content(
    after: number,
    limit: number,
    workspaceId: string | null,
  ): Promise<RemoteContent[]> {
    let query = this.client
      .from('page_updates')
      .select('id, page_id, device_id, data')
      .gt('id', after);
    if (workspaceId) query = query.eq('workspace_id', workspaceId);
    const { data, error } = await query.order('id').limit(limit);
    if (error) throw new Error(`page_updates: ${error.message}`);
    return (data ?? []).map((r) => ({
      id: Number(r.id),
      pageId: r.page_id,
      deviceId: r.device_id,
      data: fromBase64(r.data),
    }));
  }

  async uploadFile(path: string, data: Uint8Array, mime: string): Promise<void> {
    // The raw bytes as the request body, not a Blob in a multipart form: Firefox
    // sent only the form's headers for some attachments (none of the file), so
    // the proxy dropped the request and every sync round failed.
    const { error } = await this.client.storage.from(BUCKET).upload(path, data.slice(), {
      upsert: true,
      contentType: mime || 'application/octet-stream',
    });
    if (error) throw new Error(`upload ${path}: ${error.message}`);
  }

  async downloadFile(path: string): Promise<Uint8Array> {
    const { data, error } = await this.client.storage.from(BUCKET).download(path);
    if (error || !data) throw new Error(`download ${path}: ${error?.message ?? 'empty'}`);
    return new Uint8Array(await data.arrayBuffer());
  }

  async pullWorkspaces(): Promise<{ workspaces: WorkspaceRow[]; members: WorkspaceMemberRow[] }> {
    const [workspaces, members] = await Promise.all([
      this.client.from('workspaces').select('id, name, created_by, created_at'),
      this.client.from('workspace_members').select('workspace_id, user_id, permission, status'),
    ]);
    if (workspaces.error) throw new Error(`workspaces: ${workspaces.error.message}`);
    if (members.error) throw new Error(`workspace_members: ${members.error.message}`);
    return {
      workspaces: (workspaces.data ?? []).map((w) => ({
        id: w.id,
        name: w.name,
        createdBy: w.created_by,
        createdAt: new Date(w.created_at).getTime(),
      })),
      members: (members.data ?? []).map((m) => ({
        workspaceId: m.workspace_id,
        userId: m.user_id,
        permission: m.permission,
        status: m.status,
      })),
    };
  }
}
